import React, { useState, useMemo, useCallback } from 'react';
import base64url from 'base64url';
import { LoginStateContext } from './Contexts/LoginStateContext/LoginStateContext';
import { getServerURL } from '../../Utils/getServerURL';
import avenyLocalStorage from '../../Utils/LocalStorage/avenyLocalStorage';
import {
  LOCAL_STORAGE_LOGIN_USERNAME_KEY,
  LOCAL_STORAGE_LOGIN_DATABASE_KEY,
  LOCAL_STORAGE_LOGIN_SYSTEM_KEY,
  LOCAL_STORAGE_LOGIN_REMEMBER_ME_KEY,
} from '../../Utils/LocalStorage/localStorageKeys';
import EDForm from '../../BaseComponents/Forms/EDForm';
import {
  IFormIssues,
  IErrors,
  IFieldError,
  IFormBag,
  IFormValues,
} from '../../Types/FormTypes';
import { append, head } from 'ramda';
import LoginForm from './Components/LoginForm/LoginForm';
import EDDialog from '../../BaseComponents/Dialog/EDDialog';
import { Theme } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import {
  ApolloQueryResult,
  ApolloConsumer,
  useApolloClient,
} from '@apollo/client';
import VersionLink from '../../Components/AppNavbar/Components/VersionLink/VersionLink';
import Logo from '../../Assets/Branding/Aveny_logo.svg';
import classNames from 'clsx';
import { useBaseRouteParams, System } from '../Routing/Utils/routerUtils';
import databasesQuery from '../../Components/QueryComponents/DatabasesQuery/DatabasesQuery.gql';
import localDatabasesQuery from './Queries/LocalDatabasesQuery.gql';
import { DatabasesQuery as IDatabasesQueryData } from '../../Components/QueryComponents/DatabasesQuery/_QueryTypes/DatabasesQuery';
import useStateCallback from '../../Hooks/useStateCallback';
import { logLogin } from '../../Utils/analyticsUtils';
import CookieBar from '../../Components/CookieBar/CookieBar';
import { useCookieSettings } from '../../Components/CookieSettings/CookieSettingsContext';

import { CookieSettingsLink } from './Components/LoginForm/Components/CookieSettingsLink/CookieSettingsLink';
import { IConsent } from '../../Components/CookieSettings/CookieSettingsUtils';
import EDMenuButton from '../../BaseComponents/Buttons/MenuButton/EDMenuButton';
import MenuButtonItem from '../../BaseComponents/Buttons/MenuButton/EDMenuButtonItem';
import EDBox from '../../BaseComponents/EDBox/EDBox';
import { EDTeamViewerLink } from './Components/LoginForm/Components/TeamViewerLink/EDTeamViewerLink';
import EDImage from '../../BaseComponents/Image/EDImage';

interface IProps {
  formProps?: IFormBag | any;
  loginUpdate?: (dbName: string | null, system: string | null) => void;
  classes?: any;
  consent?: IConsent;
  setConsent?: (consent: IConsent) => void;
}

const styles = (theme: Theme) => ({
  dialogContentWrapper: {
    marginTop: '16px',
  },
  dialogPaper: {
    pointerEvents: 'auto',
  },
  dialogLoginPage: {
    pointerEvents: 'none',
  },
  dialogHeaderIcon: {
    marginRight: '16px',
    height: '64px',
  },
  dialogHeaderTopArea: {
    alignItems: 'center',
  },
  dialogHeaderContent: {
    minWidth: 0,
    width: '100%',
  },
});

interface ILoginInformation {
  sign: string;
  pwd: string;
  gbs: string;
  db?: string;
}

export type LoginValues = {
  system: System;
  database: string;
  username: string;
  password: string;
  passwordAutofilled: boolean;
  usernameAutofilled: boolean;
};

type RefetchAllQueriesType = () => Promise<Array<ApolloQueryResult<any>>>;

export type LoginStatus =
  | 'NO_LOGIN_IN_PROGRESS'
  | 'LOCAL_LOGIN_STARTED'
  | 'GBS_LOGIN_STARTED'
  | 'BANKID_LOGIN_STARTED';

/** Möjliga resultat vid inloggning, motsvarar TEDLoginResult i EDAvenySession */
type LoginResultCodes =
  | 'credSuccess'
  | 'credExpire'
  | 'credReset'
  | 'credChangePwd'
  | 'credReqChange'
  | 'credFail'
  | 'credMethodDenied'
  | 'credNoGBSRoles'
  | 'credUndefined';

const resetInstruction = 'Välj "Glömt inloggning?" för att byta lösenord.';
const loginResultMessage = (code: LoginResultCodes): string => {
  switch (code) {
    case 'credSuccess':
      return 'Inloggning lyckades';
    case 'credExpire':
      return `Lösenordet har gått ut. ${resetInstruction}`;
    case 'credReset':
      return `Din administratör har begärt att ditt lösenord måste ändras. ${resetInstruction}`;
    case 'credChangePwd':
      return `Lösenordet måste bytas. ${resetInstruction}`;
    case 'credReqChange':
      return `Säkerhetskraven på ditt lösenord har ändrats. ${resetInstruction}`;
    case 'credFail':
      return 'Felaktigt användarnamn eller lösenord';
    case 'credMethodDenied':
      return 'Behörighet till metod saknas i gemensamma behörighetssystemet.';
    case 'credNoGBSRoles':
      return 'Du har inga GAS-roller i gemensamma behörighetssystemet. Kontakta din behörighetsadministratör.';
    default:
      return `Okänd felkod: ${code}. Försök igen om en stund. Om felet kvarstår, kontakta support.`;
  }
};

/**
 * Komponent för att hantera inloggning i Aveny
 */
export const Login: React.FC<React.PropsWithChildren<IProps>> = props => {
  const [loginMessage, setLoginMessage] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [loginStatus, setLoginStatus] = useStateCallback<LoginStatus>(
    'NO_LOGIN_IN_PROGRESS'
  );
  const [consent, setConsent, loading] = useCookieSettings();
  const showPrivacyBar =
    (consent == null || consent.necessary == null) && !loading;
  const apolloClient = useApolloClient();
  const [{ database, system, username }, setBaseRouteParams] =
    useBaseRouteParams();
  const initialValues = useMemo(
    () => ({
      system: system,
      database: database,
      username: username,
      password: '',
      passwordAutofilled: false,
      usernameAutofilled: false,
    }),
    [system, database, username]
  );

  // After successful login we refetch all active observable queries EXCEPT the LocalDatabasesQuery.
  // We avoid refetching LocalDatabasesQuery because it does not depend on if the user is logged in or not (and it is not used after login).
  const refetchAfterLogin: RefetchAllQueriesType = async () => {
    const observableQueries = apolloClient.getObservableQueries('active');
    const refetches: Array<Promise<ApolloQueryResult<any>>> = [];
    observableQueries.forEach(oq => {
      // Skip the LocalDatabasesQuery
      if (oq.options.query === localDatabasesQuery) return;
      refetches.push(oq.refetch());

      // uncomment to debug which queries are being refetched, this should probably just be the LoginStateQuery
      // console.log('Refetching query:', oq.options.query);
    });
    return Promise.all(refetches);
  };

  const handleLogin =
    (refetchAllQueries: RefetchAllQueriesType) => (values: IFormValues) => {
      const loginValues = values as LoginValues;
      doLogin(loginValues, refetchAllQueries);

      if (rememberMe) {
        avenyLocalStorage.setItem<string>(
          LOCAL_STORAGE_LOGIN_USERNAME_KEY,
          loginValues.username
        );
        avenyLocalStorage.setItem<string>(
          LOCAL_STORAGE_LOGIN_DATABASE_KEY,
          loginValues.database
        );
        avenyLocalStorage.setItem<string>(
          LOCAL_STORAGE_LOGIN_SYSTEM_KEY,
          loginValues.system
        );
      } else {
        avenyLocalStorage.setItem<string>(LOCAL_STORAGE_LOGIN_USERNAME_KEY, '');
        avenyLocalStorage.setItem<string>(LOCAL_STORAGE_LOGIN_DATABASE_KEY, '');
        avenyLocalStorage.setItem<string>(LOCAL_STORAGE_LOGIN_SYSTEM_KEY, '');
      }

      avenyLocalStorage.setItem<boolean>(
        LOCAL_STORAGE_LOGIN_REMEMBER_ME_KEY,
        rememberMe
      );
    };

  const obfuscateLoginInfo = (info: ILoginInformation): string =>
    base64url(JSON.stringify(info), 'ascii');

  const doLogin = (
    values: LoginValues,
    refetchAllQueries: RefetchAllQueriesType
  ) => {
    const isGas = values.system === 'GAS';
    setLoginMessage(`loggar in ${values.username}...`);

    const loginInfo = {
      sign: values.username,
      pwd: values.password,
      gbs: isGas ? 'y' : 'n',
      db: values.database,
    };
    fetch(`${getServerURL()}/login`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        details: obfuscateLoginInfo(loginInfo),
      }),
    })
      .then(response => {
        if (!response.ok) {
          response.text().then(loginResult => {
            if (loginResult.startsWith('EDBackendVersionError')) {
              apolloClient.query({
                query: localDatabasesQuery,
                fetchPolicy: 'network-only',
                variables: {
                  sign: isGas && values.username ? values.username : '',
                  gbs: true,
                },
              });
              if (isGas && values.username) {
                apolloClient.query({
                  query: localDatabasesQuery,
                  fetchPolicy: 'network-only',
                  variables: {
                    sign: values.username,
                    avenyID: true,
                  },
                });
              }
            }
            setLoginMessage(
              loginResultMessage(
                head(loginResult.split(' ')) as LoginResultCodes
              )
            );
            setLoginStatus('NO_LOGIN_IN_PROGRESS');
          });
        } else {
          history.pushState({}, ''); //För att Safari ska förstå att det är dags att fråga om att spara lösenord
          apolloClient
            .query<IDatabasesQueryData, {}>({
              query: databasesQuery,
            })
            .then(_ => {
              props.loginUpdate?.(
                values.database || null,
                values.system || null
              );
              logLogin(values.system);
              refetchAllQueries().finally(() => {
                setLoginMessage('');
                setLoginStatus('NO_LOGIN_IN_PROGRESS');
              });
            });
        }
      })
      .catch(_ => {
        setLoginMessage(
          'Okänt fel: Servern svarar inte. Försök igen om en stund. Om felet kvarstår, kontakta support.'
        );
        setLoginStatus('NO_LOGIN_IN_PROGRESS');
      });
  };

  const addError = (
    fieldName: string,
    error: IFieldError,
    errors: IErrors
  ): IErrors => {
    const fieldErrors = append(error, errors[fieldName]);
    const newErrors: IErrors = { ...errors, [fieldName]: fieldErrors };
    return newErrors;
  };

  const canLogin = (values: IFormValues): IFormIssues => {
    const loginValues = values as LoginValues;
    let errors: IErrors = {};
    if (!loginValues.system) {
      errors = addError(
        'system',
        { id: 'Empty', message: 'Måste ange värde' },
        errors
      );
    }
    if (!loginValues.username && !loginValues.usernameAutofilled) {
      errors = addError(
        'username',
        { id: 'Empty', message: 'Måste ange värde' },
        errors
      );
    }
    if (!loginValues.password && !loginValues.passwordAutofilled) {
      errors = addError(
        'password',
        { id: 'Empty', message: 'Måste ange värde' },
        errors
      );
    }
    if (!loginValues.database) {
      errors = addError(
        'database',
        { id: 'Empty', message: 'Måste ange värde' },
        errors
      );
    }
    return {
      errors: errors,
      warnings: {},
    };
  };

  const handleEnterKeyUp =
    (values: LoginValues, refetchAllQueries: RefetchAllQueriesType) => () => {
      if (Object.keys(canLogin(values).errors).length === 0) {
        setLoginStatus('LOCAL_LOGIN_STARTED');
        handleLogin(refetchAfterLogin)(values);
      }
    };

  const abortLogin = useCallback(() => {
    setLoginStatus('NO_LOGIN_IN_PROGRESS', () => {});
  }, [setLoginStatus]);

  return (
    <LoginStateContext.Consumer>
      {({ isLoggedIn, databases, loading, routeIsAtLogin }) => {
        return (
          <ApolloConsumer>
            {apollo => (
              <React.Fragment>
                <EDForm
                  initialValues={initialValues}
                  onSubmit={handleLogin(refetchAfterLogin)}
                  validate={canLogin}
                  onChange={setBaseRouteParams}
                >
                  {formProps => (
                    <EDDialog
                      open={!(isLoggedIn && databases.length > 0) && !loading}
                      title="Logga in"
                      disableDrag
                      defaultCursor
                      headerProps={{
                        headerIcon: (
                          <>
                            <EDImage
                              src={Logo}
                              alt="Aveny Logo"
                              className={props.classes.dialogHeaderIcon}
                            />
                            <EDBox
                              sx={{
                                top: '0',
                                right: '0',
                                position: 'absolute',
                                color: 'white',
                              }}
                            >
                              <EDMenuButton
                                testId="login_more"
                                icon={'info'}
                                tooltip="Mer"
                                iconColor="inherit"
                                iconSize="default"
                              >
                                <MenuButtonItem key="cookieHandler">
                                  <CookieSettingsLink
                                    consent={consent}
                                    setConsent={setConsent}
                                  />
                                </MenuButtonItem>
                                <MenuButtonItem key="teamViewerDownloadLink">
                                  <EDTeamViewerLink testId="openTeamViewerDialog" />
                                </MenuButtonItem>
                              </EDMenuButton>
                            </EDBox>
                          </>
                        ),

                        classes: {
                          content: props.classes.dialogHeaderContent,
                          topArea: props.classes.dialogHeaderTopArea,
                        } as any,
                      }}
                      subtitle={<VersionLink />}
                      backdropInvisible={routeIsAtLogin}
                      classes={{
                        root: classNames({
                          [props.classes.dialogLoginPage]: routeIsAtLogin,
                        }),
                        contentWrapper: props.classes.dialogContentWrapper,
                      }}
                      PaperProps={{
                        className: props.classes.dialogPaper,
                      }}
                      noCloseIcon
                      fullScreenBreakpoint={'xs'}
                    >
                      <LoginForm
                        formProps={formProps}
                        handleEnterKeyUp={handleEnterKeyUp(
                          formProps.values as LoginValues,
                          apollo.reFetchObservableQueries
                        )}
                        rememberMe={rememberMe}
                        handleRememberMeChange={setRememberMe}
                        loginMessage={loginMessage}
                        loginStatus={loginStatus}
                        setLoginStatus={setLoginStatus}
                        consent={consent}
                        setConsent={setConsent}
                        onAbortLogin={abortLogin}
                        setLoginMessage={setLoginMessage}
                      />
                    </EDDialog>
                  )}
                </EDForm>
                {showPrivacyBar && (
                  <CookieBar consent={consent} setConsent={setConsent} />
                )}
              </React.Fragment>
            )}
          </ApolloConsumer>
        );
      }}
    </LoginStateContext.Consumer>
  );
};

export default withStyles(Login, styles);
